<script lang="ts">
  import Accordion from '$components/accordion.svelte';
  import Button from '$components/ui/button.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import Input from '$components/ui/input.svelte';
  import { addDiscountToCart, addToCart } from '$lib/cart';
  import { t } from '$lib/i18n';
  import { cart } from '$lib/cart';
  import { createQuery } from '@tanstack/svelte-query';
  import { shopify } from '$lib/shopify';
  import { page } from '$app/stores';
  import { region } from '$lib/region';
  import type { ProductDetailPageStoryblok } from '$types/bloks';
  import type { StoryblokStory } from 'storyblok-generate-ts';

  let value: string | undefined = undefined;
  let isLoading = false;
  let hasError = false;
  let discountCodes =
    $cart?.discountCodes.filter((code) => code.applicable).map((discount) => discount.code) || [];

  $: discountCode =
    $region == 'us'
      ? $page.data.configuration.content.discount_code_us
      : $region == 'uk'
        ? $page.data.configuration.content.discount_code_uk
        : $page.data.configuration.content.discount_code_eu;

  $: giftHandle = (
    ($region == 'us'
      ? $page.data.configuration.content.gift_us
      : $region == 'uk'
        ? $page.data.configuration.content.gift_uk
        : $page.data.configuration.content.gift_eu) as StoryblokStory<ProductDetailPageStoryblok>
  )?.slug;

  $: giftIdQuery = giftHandle
    ? createQuery({
        staleTime: 5 * 60 * 1000,
        queryKey: ['getProductId', giftHandle],
        queryFn: async () => {
          const product = await $shopify.getProduct({ handle: giftHandle });

          return product;
        }
      })
    : null;

  $: giftProduct = $giftIdQuery?.data?.product;

  export const addGiftToCart = async () => {
    if (!giftProduct) return;

    const variant = giftProduct.variants.nodes.find((v) => {
      return v.availableForSale && v.quantityAvailable && v.quantityAvailable > 0;
    });

    const alreadyInCart = $cart?.lines.nodes.some((l) => l.merchandise.id == variant?.id);

    if (variant && !alreadyInCart) {
      await $addToCart([{ merchandiseId: variant.id, quantity: 1 }]);
    }
  };

  const addOrRemoveDiscounts = async (code?: string, isRemoved?: boolean) => {
    isLoading = true;
    hasError = false;

    try {
      let result: string[] = [...discountCodes];

      if (value && !result.includes(value)) {
        result.push(value);
      } else {
        result = discountCodes.filter((item) => item !== code);
      }

      await $addDiscountToCart(result, isRemoved);

      if (value?.toUpperCase() == discountCode?.toUpperCase()) {
        await addGiftToCart();
      }

      if (!isRemoved) {
        hasError = !$cart?.discountCodes.find((code) => code.code === value)?.applicable;
      }

      discountCodes =
        $cart?.discountCodes.filter((item) => item.applicable).map((discount) => discount.code) ||
        [];

      value = '';
    } catch (error) {
      hasError = true;
    } finally {
      isLoading = false;
    }
  };
</script>

<div class="mt-2">
  <Accordion title={$t('discount.form.title')} icon="discount" variant="discount">
    <form
      class="w-full"
      on:submit={(e) => {
        e.preventDefault();
        addOrRemoveDiscounts();
      }}
    >
      <div class="flex gap-2">
        <div class="w-full">
          <Input
            type="text"
            bind:value
            placeholder={$t('discount.form.placeholder')}
            class="w-full h-10 [&_input]:h-10"
            inputClass="text-sm"
            error={hasError}
          />

          {#if hasError}
            <p class="text-danger mt-2 text-sm">{$t('discount.form.error')}</p>
          {/if}
        </div>

        <Button
          name="add_discount"
          variant="primary"
          size="sm"
          disabled={!value}
          loading={isLoading}
          class="px-3"
          type="submit"
        >
          {$t('discount.form.apply')}
        </Button>
      </div>

      {#each discountCodes as code}
        <button
          class="inline-flex w-auto items-center gap-1 mr-1 bg-highlight/80 text-primary rounded-xs mt-3 py-[3px] pl-[6px] pr-[4px]"
          type="button"
          on:click={() => addOrRemoveDiscounts(code, true)}
        >
          <Icon icon="discount" size="md" class="text-accent" />
          <span class="text-sm font-semibold uppercase">{code}</span>
          <Icon icon="cross" size="md" />
        </button>
      {/each}
    </form>
  </Accordion>
</div>
